"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { PhoneCaptureCard } from "@/components/outfame/PhoneCaptureCard";
import { useDemoContext } from "@/contexts/DemoContext";
import { useNotificationStatus } from "@/hooks/queries/notifications";
const DISMISS_KEY = "outfame:growth-report-banner-dismissed";
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;
interface GrowthReportBannerProps {
  className?: string;
}
export default function GrowthReportBanner(t0) {
  const $ = _c(13);
  const {
    className
  } = t0;
  const demoContext = useDemoContext();
  const [expanded, setExpanded] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [isDismissedLocally, setIsDismissedLocally] = useState(true);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const dismissed = localStorage.getItem(DISMISS_KEY);
      if (dismissed) {
        const dismissedAt = parseInt(dismissed, 10);
        if (!isNaN(dismissedAt) && Date.now() - dismissedAt < SEVEN_DAYS_MS) {
          setIsDismissedLocally(true);
          return;
        }
      }
      setIsDismissedLocally(false);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  const t3 = !isDismissedLocally;
  let t4;
  if ($[2] !== t3) {
    t4 = {
      defer: true,
      enabled: t3
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const {
    data: notificationData,
    isLoading
  } = useNotificationStatus(t4);
  const hasVerifiedChannel = notificationData?.channels?.some(_temp) ?? false;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      localStorage.setItem(DISMISS_KEY, Date.now().toString());
      setDismissing(true);
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const handleDismiss = t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      setExpanded(true);
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const handleEnable = t6;
  if (demoContext?.isDemoMode) {
    return null;
  }
  if (isDismissedLocally || isLoading || hasVerifiedChannel) {
    return null;
  }
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => setExpanded(false);
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== className || $[8] !== dismissing || $[9] !== expanded) {
    t8 = !dismissing && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: "easeOut"
    }} className={cn("relative rounded-2xl border border-gray-100 bg-gradient-to-r from-green-50/50 to-emerald-50/30 p-5", className)}><button type="button" onClick={handleDismiss} className="absolute right-3 top-3 rounded-md p-1 text-gray-300 transition-colors hover:text-gray-500" aria-label="Dismiss"><svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg></button><div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-5"><div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#25D366]"><svg className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" /></svg></div><div className="min-w-0 flex-1"><p className="text-[15px] font-semibold text-gray-900">Get your weekly growth report</p><p className="mt-0.5 text-[13px] text-gray-500">See exactly how your account grew — delivered to WhatsApp</p></div>{!expanded && <button onClick={handleEnable} className="shrink-0 rounded-xl bg-gray-900 px-4 py-2 text-[13px] font-medium text-white transition-colors hover:bg-gray-800">Enable reports</button>}</div><AnimatePresence>{expanded && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: "auto",
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.25,
          ease: [0.22, 1, 0.36, 1]
        }} className="overflow-hidden"><div className="pt-4"><PhoneCaptureCard variant="banner" onPhoneCaptured={handleDismiss} /></div></motion.div>}</AnimatePresence></motion.div>;
    $[7] = className;
    $[8] = dismissing;
    $[9] = expanded;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t8) {
    t9 = <AnimatePresence onExitComplete={t7}>{t8}</AnimatePresence>;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  return t9;
}
function _temp(ch) {
  return (ch.channel_type === "whatsapp" || ch.channel_type === "telegram") && ch.is_verified;
}